import { ActionIcon, Alert, Button, Group, NumberInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconClockPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { datum, datumZaApi, kolicina } from '../oblika/oblika';
import { useSeja } from '../seja/useSeja';
import { obrezano } from './delavnica';

type Nalog = Shema['DelovniNalogPodrobnosti'];

/**
 * Opravljene ure na nalogu: mehanik vpiše ure (privzeto sebe in danes) s kratkim opisom, brez urejanja naloga.
 * Postavke dela se iz ur razporedijo posebej (»Razporedi ure« pri postavkah). Po prevzemu samo ogled.
 */
export function UreNaloga({ nalog, posodobljen }: { nalog: Nalog; posodobljen: (n: Nalog) => void }) {
  const stanje = useSeja();
  const jaz = stanje.status === 'prijavljen' ? stanje.uporabnik.prijavaId : undefined;
  const [mehanik, setMehanik] = useState<string | null>(jaz ? String(jaz) : null);
  const [dan, setDan] = useState<string | null>(null);
  const [ure, setUre] = useState<number | string>('');
  const [opis, setOpis] = useState('');
  const [polja, setPolja] = useState<Record<string, string>>({});
  const moznosti = useQuery({
    queryKey: ['delavnica-moznosti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/nalogi/moznosti', { signal })),
    enabled: !!nalog.urejanje,
  });

  const dodaj = useMutation({
    mutationFn: () =>
      podatki(
        api.POST('/api/delavnica/nalogi/{id}/ure', {
          params: { path: { id: nalog.id! } },
          body: {
            mehanikId: mehanik ? Number(mehanik) : undefined,
            datum: dan ?? undefined,
            ure: typeof ure === 'number' ? ure : undefined,
            opis: obrezano(opis),
          },
        }),
      ),
    onMutate: () => setPolja({}),
    onSuccess: (n) => {
      setUre('');
      setOpis('');
      posodobljen(n);
    },
    onError: (e) => e instanceof NapakaApi && setPolja(e.polja),
  });
  const odstrani = useMutation({
    mutationFn: (uraId: number) =>
      podatki(api.DELETE('/api/delavnica/nalogi/{id}/ure/{uraId}', { params: { path: { id: nalog.id!, uraId } } })),
    onSuccess: posodobljen,
  });

  const vpisi = nalog.ure ?? [];
  const napaka = dodaj.error ?? odstrani.error;
  return (
    <Stack gap="sm">
      {vpisi.length > 0 ? (
        <Table.ScrollContainer minWidth={480}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Datum</Table.Th>
                <Table.Th>Mehanik</Table.Th>
                <Table.Th ta="right">Ure</Table.Th>
                <Table.Th>Opis</Table.Th>
                {nalog.urejanje && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vpisi.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td className="brez-preloma">{datum(u.datum)}</Table.Td>
                  <Table.Td>{u.mehanik}</Table.Td>
                  <Table.Td ta="right" className="brez-preloma">
                    {kolicina(u.ure)} h
                  </Table.Td>
                  <Table.Td>{u.opis}</Table.Td>
                  {nalog.urejanje && (
                    <Table.Td ta="right">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Izbriši ure ${datum(u.datum)}`}
                        onClick={() => u.id && odstrani.mutate(u.id)}
                        loading={odstrani.isPending && odstrani.variables === u.id}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <Text size="sm" c="dimmed">
          Ni vpisanih ur.
        </Text>
      )}
      {nalog.urejanje && (
        <Group gap="xs" align="flex-start" wrap="wrap">
          <Select
            label="Mehanik"
            data={(moznosti.data?.mehaniki ?? []).map((m) => ({ value: String(m.id), label: m.ime ?? '' }))}
            value={mehanik}
            onChange={setMehanik}
            error={polja.mehanikId}
            allowDeselect={false}
            w={{ base: '100%', xs: 200 }}
          />
          <DatePickerInput
            label="Datum"
            placeholder="Danes"
            value={dan}
            onChange={(d) => setDan(datumZaApi(d) ?? null)}
            valueFormat="D. M. YYYY"
            clearable
            w={{ base: 'calc(50% - 4px)', xs: 150 }}
          />
          <NumberInput
            label="Ure"
            value={ure}
            onChange={setUre}
            error={polja.ure}
            decimalScale={2}
            decimalSeparator=","
            step={0.25}
            allowNegative={false}
            w={{ base: 'calc(50% - 4px)', xs: 100 }}
          />
          <TextInput
            label="Opis"
            value={opis}
            onChange={(e) => setOpis(e.currentTarget.value)}
            onBlur={() => setOpis((o) => o.trim())}
            error={polja.opis}
            style={{ flex: '1 1 200px' }}
          />
          <Button
            mt={{ base: 0, xs: 26 }}
            leftSection={<IconClockPlus size={18} />}
            onClick={() => dodaj.mutate()}
            loading={dodaj.isPending}
          >
            Vpiši ure
          </Button>
        </Group>
      )}
      {napaka && !(napaka instanceof NapakaApi && Object.keys(napaka.polja).length > 0) && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(napaka)}
        </Alert>
      )}
    </Stack>
  );
}
